import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import {
  ServiceName,
  ServiceNameKey,
  SERVICE_NAME_VALUES,
} from './service-order.dto';

// =================================================================================================
// >> UTILS
// =================================================================================================
class TotalesDto {
  @ApiProperty()
  montoTotal!: number;
}

class BaseRowDto {
  @ApiProperty()
  numeroLiquidacion!: string;

  @ApiProperty()
  exportador!: string;

  @ApiProperty()
  refCliente!: string;
}

// =================================================================================================
// >> PALLETS
// =================================================================================================
export class AgenciaExcelPltsRowDto extends BaseRowDto {
  @ApiProperty()
  nave!: string;

  @ApiProperty()
  pol!: string;

  @ApiProperty()
  etd!: string;

  @ApiProperty()
  especie!: string;

  @ApiProperty()
  plts!: number;

  @ApiProperty()
  pesoKg!: number;

  @ApiProperty()
  volumenM3!: number;

  @ApiProperty({ required: false })
  valorUnitario?: number;

  @ApiProperty({
    example: 'CONSOLIDADO',
    enum: ['CONSOLIDADO', 'FCL', 'TRANSPORTE', ''],
  })
  tipoServicio!: 'CONSOLIDADO' | 'FCL' | 'TRANSPORTE' | '';

  @ApiProperty({ required: false })
  transporte?: number;

  @ApiProperty({ required: false })
  sobrestadiaPuerto?: number;

  @ApiProperty()
  agenciamiento!: number;

  @ApiProperty()
  dusLegalizado!: number;

  @ApiProperty({ required: false })
  gestionOpbNeto?: number;

  @ApiProperty()
  totalVentaNeto!: number;

  @ApiProperty()
  totalTraspasoBruto!: number;

  @ApiProperty()
  observaciones!: string;
}

export class TotalesPorEspecieDto {
  @ApiProperty()
  especie!: string;

  @ApiProperty()
  plts!: number;

  @ApiProperty()
  pesoKg!: number;
}

// ✅ DTO ESPECÍFICO para pallets.builder.ts
export class AgenciaExcelPltsSeccionDto {
  @ApiProperty({ type: [AgenciaExcelPltsRowDto] })
  rows!: AgenciaExcelPltsRowDto[];

  @ApiProperty({ type: [TotalesPorEspecieDto] })
  totalesPorEspecie!: TotalesPorEspecieDto[];

  @ApiProperty()
  totalPesoKg!: number;

  @ApiProperty()
  totalPlts!: number;
}

// ✅ Alias para mantener compatibilidad con código anterior
export class AgenciaExcelPalletsDto {
  @ApiProperty({ type: [AgenciaExcelPltsRowDto] })
  rows!: AgenciaExcelPltsRowDto[];

  @ApiProperty()
  totales!: TotalesDto;
}

// =================================================================================================
// >> CONTENEDORES
// =================================================================================================

/**
 * Tipo para servicios dinámicos de contenedores.
 * Las keys son las constantes normalizadas de ServiceName (ej: SERVICIO_GATE_OUT).
 * Los valores son los montos en número.
 */
export type ContenedorServiciosDinamicos = Partial<
  Record<ServiceNameKey, number>
>;

/**
 * DTO para una fila de contenedor en el Excel de liquidaciones agencia.
 *
 * Estructura híbrida:
 * - Campos fijos: siempre presentes (nave, pol, etd, nroContenedor, etc.)
 * - Campos de servicios dinámicos: objeto `servicios` con keys normalizadas
 *
 * @example
 * {
 *   "nave": "EVER LAMBENT",
 *   "pol": "VALPARAISO",
 *   "etd": "05-11-2025",
 *   "exportador": "ALMAHUE",
 *   "nroContenedor": "FBIU5329661",
 *   "refCliente": "2",
 *   "plts": 20,
 *   "agenciamiento": 99500,
 *   "transporte": 650000,
 *   "gestionOpbNeto": 8000,
 *   "totalVentaNeto": 862500,
 *   "gestionOpbTraspaso": 0,
 *   "totalTraspasoBruto": 165152,
 *   "servicios": {
 *     "SERVICIO_GATE_OUT": 105000,
 *     "BL_FEE": 47600,
 *     "CERTIFICADO_ORIGEN_UCCO_CHINA_EUR": 10000,
 *     "DUS_LEGALIZADO": 12000,
 *     "SECURITY_FEE_CONTENEDOR": 17012
 *   }
 * }
 */
export class AgenciaExcelCntrRowDto extends BaseRowDto {
  // ==================== CAMPOS FIJOS ====================

  @ApiProperty({ description: 'Nombre de la nave', example: 'EVER LAMBENT' })
  nave!: string;

  @ApiProperty({ description: 'Puerto de carga (POL)', example: 'VALPARAISO' })
  pol!: string;

  @ApiProperty({ description: 'Fecha ETD', example: '05-11-2025' })
  etd!: string;

  @ApiProperty({ description: 'Número de contenedor', example: 'FBIU5329661' })
  nroContenedor!: string;

  @ApiProperty({ description: 'Cantidad de pallets', example: 20 })
  plts!: number;

  @ApiProperty({ description: 'Monto de agenciamiento', example: 99500 })
  agenciamiento!: number;

  @ApiProperty({ description: 'Monto de transporte', example: 650000 })
  transporte!: number;

  @ApiProperty({ description: 'Gestión OPB Neto (Venta)', example: 8000 })
  gestionOpbNeto!: number;

  @ApiProperty({ description: 'Total Venta Neto', example: 862500 })
  totalVentaNeto!: number;

  @ApiProperty({ description: 'Gestión OPB Traspaso', example: 0 })
  gestionOpbTraspaso!: number;

  @ApiProperty({ description: 'Total Traspaso Bruto', example: 165152 })
  totalTraspasoBruto!: number;

  // ==================== SERVICIOS DINÁMICOS ====================

  /**
   * Servicios dinámicos asociados al contenedor.
   * Solo se incluyen los servicios que tienen monto > 0.
   * Las keys son constantes normalizadas (ej: SERVICIO_GATE_OUT, DUS_LEGALIZADO).
   * Para obtener el nombre legible, usar: ServiceName[key]
   */
  @ApiPropertyOptional({
    description:
      'Servicios dinámicos del contenedor. Keys son constantes normalizadas de ServiceName.',
    example: {
      SERVICIO_GATE_OUT: 105000,
      PORTEOS: 0,
      SOBRESTADIA_EN_DEPOSITO: 0,
      SOBRESTADIA_EN_PLANTA: 0,
      BL_FEE: 47600,
      CAMBIO_DE_SELLOS: 0,
      CERTIFICADO_ORIGEN_UCCO_CHINA_EUR: 10000,
      CERTIFICADO_EXTRA_ORIGEN_UCCO_CHINA_EUR: 0,
      CORRECCION_DE_BL: 78540,
      DUS_LEGALIZADO: 12000,
      SECURITY_FEE_CONTENEDOR: 17012,
    },
  })
  servicios?: ContenedorServiciosDinamicos;
}

// ✅ DTO ESPECÍFICO para contenedores.builder.ts
export class AgenciaExcelCntrSeccionDto {
  @ApiProperty({ type: [AgenciaExcelCntrRowDto] })
  rows!: AgenciaExcelCntrRowDto[];

  @ApiProperty()
  totalesPorNaviera!: Array<{
    naviera: string;
    cntrs: number;
    montoUsd: number;
  }>;

  @ApiProperty()
  totalesPorPuerto!: Array<{ puerto: string; cntrs: number; montoUsd: number }>;
}

// ✅ Alias para mantener compatibilidad con código anterior
export class AgenciaExcelContenedoresDto {
  @ApiProperty({ type: [AgenciaExcelCntrRowDto] })
  rows!: AgenciaExcelCntrRowDto[];

  @ApiProperty()
  totales!: TotalesDto;
}

// =================================================================================================
// >> SERVICIOS EXTRA
// =================================================================================================
export class AgenciaExcelExtraRowDto extends BaseRowDto {
  @ApiProperty()
  nroOs!: string;

  @ApiProperty({
    description: 'Nombre del servicio asociado a la liquidación',
    example: ServiceName.certificadoFitosanitario,
    enum: SERVICE_NAME_VALUES,
  })
  servicio!: string;

  @ApiProperty({ example: 'VENTA', enum: ['VENTA', 'TRASPASO', ''] })
  tipoFacturacion!: 'VENTA' | 'TRASPASO' | '';

  @ApiProperty()
  cantidad!: number;

  @ApiProperty()
  totalVentaNeto!: number;

  @ApiProperty()
  totalTraspasoBruto!: number;

  @ApiProperty()
  gestionOpbNeto!: number;

  @ApiProperty()
  comentarios!: string;
}

export class TotalesPorTipoServicioDto {
  @ApiProperty({
    description: 'Nombre del tipo de servicio',
    example: ServiceName.certificadoFitosanitario,
    enum: SERVICE_NAME_VALUES,
  })
  tipoServicio!: string;

  @ApiProperty({ description: 'Monto total del servicio', example: 15000 })
  monto!: number;
}

// ✅ DTO ESPECÍFICO para servicios.builder.ts
export class AgenciaExcelExtraSeccionDto {
  @ApiProperty({ type: [AgenciaExcelExtraRowDto] })
  rows!: AgenciaExcelExtraRowDto[];

  @ApiProperty({ type: [TotalesPorTipoServicioDto] })
  totalesPorTipoServicio!: TotalesPorTipoServicioDto[];

  @ApiProperty()
  montoTotal!: number;
}

// ✅ Alias para mantener compatibilidad con código anterior
export class AgenciaExcelServiciosExtraDto {
  @ApiProperty({ type: [AgenciaExcelExtraRowDto] })
  rows!: AgenciaExcelExtraRowDto[];

  @ApiProperty({ type: [TotalesPorTipoServicioDto] })
  totalesPorTipoServicio!: TotalesPorTipoServicioDto[];

  @ApiProperty()
  montoTotal!: number;
}

// =================================================================================================
// >> TOTALES (para totales.builder.ts)
// =================================================================================================
export class AgenciaExcelTotalesMesItemDto {
  @ApiProperty()
  mes!: string;

  @ApiProperty()
  cntrs!: number;

  @ApiProperty()
  plts!: number;

  @ApiProperty()
  montoUsd!: number;

  @ApiProperty()
  montoClp!: number;
}

export class AgenciaExcelTotalesDetalleRowDto {
  @ApiProperty()
  numeroLiquidacion!: string;

  @ApiProperty()
  nave!: string;

  @ApiProperty()
  pol!: string;

  @ApiProperty()
  etd!: string;

  @ApiProperty()
  exportador!: string;

  @ApiProperty()
  cntrs!: number;

  @ApiProperty()
  sConv!: number;

  @ApiProperty()
  plts!: number;

  @ApiProperty()
  cajas!: number;

  @ApiProperty()
  montoAge!: number;

  @ApiProperty()
  montoTransporte!: number;

  @ApiProperty()
  montoOrdenesServicios!: number;

  @ApiProperty()
  gestionOpbNeto!: number;

  @ApiProperty()
  totalVentaNeto!: number;

  @ApiProperty()
  totalTraspasoBruto!: number;
}

// ✅ DTO ESPECÍFICO para totales.builder.ts
export class AgenciaExcelSeccionTotalesDto {
  @ApiProperty()
  numeroLiquidacion!: string;

  @ApiProperty()
  cliente!: string;

  @ApiProperty()
  fechaGeneracion!: string;

  @ApiProperty()
  temporada!: string;

  @ApiProperty()
  totalContenedores!: number;

  @ApiProperty()
  totalPallets!: number;

  @ApiProperty()
  totalServiciosExtraUsd!: number;

  @ApiProperty()
  totalUsd!: number;

  @ApiProperty()
  totalClp!: number;

  @ApiProperty()
  descuentosUsd!: number;

  @ApiProperty()
  descuentosClp!: number;

  @ApiProperty()
  totalFacturado!: number;

  @ApiProperty()
  pendientePago!: number;

  @ApiProperty()
  estado!: string;

  @ApiProperty({ type: [AgenciaExcelTotalesMesItemDto] })
  totalesPorMes!: AgenciaExcelTotalesMesItemDto[];

  @ApiProperty({ type: [AgenciaExcelTotalesDetalleRowDto] })
  rows!: AgenciaExcelTotalesDetalleRowDto[];
}

// =================================================================================================
// >> MAIN DTO
// =================================================================================================
export class AgenciaExcelDataDto {
  @ApiProperty({ type: AgenciaExcelSeccionTotalesDto })
  totales!: AgenciaExcelSeccionTotalesDto;

  @ApiProperty({ type: AgenciaExcelContenedoresDto })
  contenedores!: AgenciaExcelContenedoresDto;

  @ApiProperty({ type: AgenciaExcelPalletsDto })
  pallets!: AgenciaExcelPalletsDto;

  @ApiProperty({ type: AgenciaExcelServiciosExtraDto })
  serviciosExtra!: AgenciaExcelServiciosExtraDto;
}
